/**
 * Cacheable Utility.
 *
 * Abstraction for managing cache-able data objects.
 *
 */
// imports
import Store from './store';


/**
 * GetterFunction Interface
 * 
 * A function that calculates the value and
 * always returns a string value ('' for null)
 * 
 * Each GetterFunction is expected to be unique for a given
 * piece of data. Therefore no function arguments are supported.
 */
interface GetterFunction extends CallableFunction {
    (): Array<string>;
}


/**
 * Cacheable Utility class.
 */
export default class Cacheable {
    
    // attribute initilization
    public store: Store;

    /**
     * Cacheable constructor.
     *
     * Setup the store for reference through the lifecycle of the object.
     */
    constructor() {
        this.store = new Store();
    }

    /**
     * Get the value from client side storage by key.
     * Fall back to the value returned by func.
     *
     * @param {string} key
     * @param {GetterFunction} func
     * @return {string}
     */
    public get(key: string, func: GetterFunction, unacceptable: Array<unknown> = []): Array<string> {
        if (unacceptable.length === 0) {
            // by default, these are the unacceptable values
            unacceptable = ['', false, null, undefined];
        }
        // check storage for the value
        const values: Array<string> = this.store.get(key);
        const cleanValues = [];
        for (let i = 0; i < values.length; i ++) {
            if (unacceptable.indexOf(values[i]) === -1) {
                cleanValues.push(values[i]);
            }
        }
        // if the value is unacceptable, then get its non-cached value and set it
        if (cleanValues.length === 0) {
            const funcValue: Array<string> = func();
            this.store.set(key, funcValue);
            return funcValue;
        }
        this.store.set(key, cleanValues);
        return cleanValues;
    }

}
